<template>
  <div class="min-h-screen flex items-center justify-center bg-background">
    <div class="p-8 max-w-md w-full bg-card border border-border rounded-lg shadow-md">
      <div class="text-center">
        <h1 class="text-2xl font-bold text-foreground mb-2">{{ status }}</h1>
        <p class="text-muted-foreground mb-6">{{ message }}</p>
        <div v-if="loading" class="flex justify-center mb-4">
          <div class="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div v-if="errorDetails" class="mt-4 p-4 bg-red-500/10 border border-red-500/30 text-left rounded-lg">
          <p class="text-sm text-red-700 dark:text-red-300 font-mono whitespace-pre-wrap">{{ errorDetails }}</p>
        </div>
        <div v-if="!loading" class="mt-6 space-y-3">
          <button
            @click="goToLogin"
            class="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors w-full"
          >
            Return to Login
          </button>
          <button
            v-if="!success && ticket"
            @click="retryAuthentication"
            class="px-4 py-2 bg-background border border-primary text-primary rounded-lg hover:bg-accent transition-colors w-full"
          >
            Retry Authentication
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import apiClient from '@/services/api';
import { redeemInviteCode } from '@/utils/inviteLink';
import authStore from '@/store/auth';

export default {
  name: 'OAuthCallback',
  setup() {
    const router = useRouter();
    const route = useRoute();
    
    const loading = ref(true);
    const status = ref('Completing Authentication...');
    const message = ref('Please wait while we complete your authentication');
    const errorDetails = ref('');
    const success = ref(false);
    const state = ref('');
    const ticket = ref('');
    
    const goToLogin = () => {
      router.push('/login');
    };
    
    const retryAuthentication = () => {
      loading.value = true;
      status.value = 'Retrying Authentication...';
      message.value = 'Please wait while we try again...';
      errorDetails.value = '';
      handleOAuthCallback();
    };
    
    // Function to handle the OAuth code
    const handleOAuthCallback = async () => {
      // Store the ticket and state for potential retries. The backend swaps
      // the raw OAuth code for a short-lived opaque ticket before redirecting
      // here, so the code itself never lands in the browser URL/history twice.
      ticket.value = route.query.ticket || '';
      state.value = route.query.state || '';
      // Dynamically determine the host for the backend
      // Use window.location.host which includes the port (e.g., localhost:8080)
      let host = window.location.host;

      if (!ticket.value) {
        status.value = 'Authentication Failed';
        message.value = 'No authentication code was received from the provider.';
        loading.value = false;
        return;
      }


      try {
        // Call API to complete authentication using the ticket
        const response = await apiClient.post('/auth/google-callback/', {
          ticket: ticket.value,
          state: state.value,
          host: host
        });
        
        // Store auth token
        const token = response.data.token;
        localStorage.setItem('token', token);
        
        // Set auth header for future API calls
        apiClient.defaults.headers.common['Authorization'] = `Token ${token}`;

        // A new Google signup from a campaign link gets OLJ jobs before we load the user.
        await redeemInviteCode(apiClient);
        
        // Check if user has already completed onboarding by fetching the latest user data
        try {
          const userResponse = await apiClient.get('/auth/user/');
          // Update auth store with the latest user data, which includes onboarding status
          authStore.setAuth(true, userResponse.data);
          
          // Use the user data from response to determine onboarding status
          // This ensures we have the most up-to-date information
          const userData = userResponse.data;
          
          // Force server to check if onboarding should be marked as complete
          // This addresses the issue where onboarding is not marked as complete
          // after Google OAuth login even when the user has completed all required steps
          if (userData && userData.account_type === 'jobseeker') {
            try {
              await apiClient.post('/auth/check-onboarding-status/');
              
              // Log the detailed check results to help debug
              
              // Re-fetch user data to get updated onboarding status
              const refreshedUserResponse = await apiClient.get('/auth/user/');
              authStore.setAuth(true, refreshedUserResponse.data);
              userData.onboarding_completed = refreshedUserResponse.data.onboarding_completed;
            } catch (checkError) {
              console.error('Error checking onboarding status:', checkError);
            }
          }
          
          // Redirect to stored path, or /onboarding for new jobseekers, else /dashboard
          const _storedRedirect = localStorage.getItem('oauth_redirect');
          localStorage.removeItem('oauth_redirect');
          const oauthRedirect = _storedRedirect ||
            (userData && userData.account_type === 'jobseeker' && !userData.onboarding_completed
              ? '/onboarding'
              : '/dashboard');
          localStorage.setItem('showOnboardingModal', 'true');
          status.value = 'Authentication Successful';
          message.value = 'You have been successfully authenticated. Redirecting...';
          success.value = true;
          setTimeout(() => {
            router.push(oauthRedirect);
          }, 1000);

        } catch (userError) {
          console.error('Error fetching user data after OAuth login:', userError);

          // Fall back to the data from the original response if we can't fetch user data
          authStore.setAuth(true, response.data.user);
          const _fallbackUser = response.data.user;

          const _storedRedirect2 = localStorage.getItem('oauth_redirect');
          localStorage.removeItem('oauth_redirect');
          const oauthRedirect = _storedRedirect2 ||
            (_fallbackUser && _fallbackUser.account_type === 'jobseeker' && !_fallbackUser.onboarding_completed
              ? '/onboarding'
              : '/dashboard');

          // Show success message briefly before redirecting
          status.value = 'Authentication Successful';
          message.value = 'You have been successfully authenticated. Redirecting...';
          success.value = true;

          // Redirect after a short delay
          setTimeout(() => {
            router.push(oauthRedirect);
          }, 1000);
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        success.value = false;
        
        status.value = 'Authentication Failed';
        
        if (error.response?.data?.error) {
          message.value = error.response.data.error;
          if (error.response.data.details) {
            errorDetails.value = error.response.data.details;
          }
          
          // Check for specific redirect_uri_mismatch error
          if (error.response.data.error.includes('redirect_uri_mismatch')) {
            message.value = 'OAuth configuration error. The redirect URI is incorrect.';
            errorDetails.value = 'There is a mismatch between the redirect URI registered in Google Cloud Console and the one used by the application.\n\nPlease check that this URI is registered in your Google Cloud Console:\n- https://applypa.com/api/auth/google/callback/\n- https://www.applypa.com/api/auth/google/callback/';
          }
          
          // Check for backend errors
          if (error.response.status === 500) {
            message.value = 'A server error occurred during authentication.';
            errorDetails.value = error.response.data.details || 'Please try again or contact support if the problem persists.';
          }
        } else {
          message.value = 'There was a problem completing your authentication. Please try again.';
          if (error.message) {
            errorDetails.value = `Error: ${error.message}`;
          }
        }
      } finally {
        loading.value = false;
      }
    };
    
    onMounted(() => {
      handleOAuthCallback();
    });
    
    return {
      loading,
      status,
      message,
      errorDetails,
      success,
      ticket,
      state,
      goToLogin,
      retryAuthentication
    };
  }
};
</script> 